import _ from 'lodash';
import getAngularModule from 'App/babel/angularHelpers/getAngularModule';
import logError from 'Helpers/logError';
import ValidationService from 'Services/ValidationService';
import AppService from 'App/legacy/AppService';
import type Contact from 'App/resources/Model/Contact';

type DuplicateParams = {
	AllowDuplicateContacts?: boolean;
	AllowCrossCompanyDuplicateContacts?: boolean;
};

export type BlockingDuplicate = {
	type: 'sameCompany' | 'crossCompany';
	contacts: Contact[];
};

/** Splits email duplicates into the ones on the given company and the ones on other companies. */
export const partitionDuplicates = (duplicates: Contact[], clientId?: number) =>
	_.partition(duplicates, duplicate => duplicate.client?.id === clientId);

/**
 * The rule for when a duplicate email address blocks saving a contact. Every contact form asks this
 * instead of reading the two client params itself.
 *
 * Returns the duplicates that block the save, or null when the save is allowed.
 */
export const getBlockingDuplicate = (
	sameCompanyDuplicates: Contact[],
	crossCompanyDuplicates: Contact[]
): BlockingDuplicate | null => {
	const params = (AppService.getMetadata()?.params ?? {}) as DuplicateParams;

	if (!params.AllowDuplicateContacts && sameCompanyDuplicates.length) {
		return { type: 'sameCompany', contacts: sameCompanyDuplicates };
	}
	if (!params.AllowCrossCompanyDuplicateContacts && crossCompanyDuplicates.length) {
		return { type: 'crossCompany', contacts: crossCompanyDuplicates };
	}
	return null;
};

/**
 * Looks the email up and applies the rule above — for forms that keep no duplicate list of their
 * own, such as the quick create/edit contact form in the order modal.
 */
export const findBlockingDuplicate = async (
	clientId: number | undefined,
	contactId: number | undefined,
	email: string | undefined
): Promise<BlockingDuplicate | null> => {
	if (!clientId || !email || !ValidationService.validEmail(email)) {
		return null;
	}

	let duplicates: Contact[] = [];
	try {
		const emailDuplicates = getAngularModule('EmailDuplicates') as {
			get: (clientId: number, excludedId: number | undefined, email: string) => Promise<{ data: Contact[] }>;
		};
		duplicates = (await emailDuplicates.get(clientId, contactId, email)).data ?? [];
	} catch (e) {
		logError(e as Error, 'Failed to get email duplicates');
		return null;
	}

	const [sameCompanyDuplicates, crossCompanyDuplicates] = partitionDuplicates(duplicates, clientId);
	return getBlockingDuplicate(sameCompanyDuplicates, crossCompanyDuplicates);
};
